/**
 * Custom hook and store for Botpress Webchat integration.
 * Manages global chat state (open/close) and user context synchronization.
 */
"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/hooks/use-auth";
import { usePathname } from "next/navigation";
import { useCallback } from "react";
export interface BotPressUserData {
  name: string | undefined;
  pictureUrl: string | undefined;
  data: {
    name: string;
    pictureUrl: string;
    email: string;
    phone: string;
    id: string;
    url: string;
    pathname: string;
    pageSection: string;
  };
}
export interface WindowBotPress {
  getUserData: () => BotPressUserData;
  open: () => void;
  close: () => void;
  sendEvent: (event: {
    type: string;
    payload?: Record<string, unknown>;
  }) => void;
  sendMessage: (message: string) => void;
  updateUser: (userData: BotPressUserData) => void;
  on: (event: string, callback: (data?: unknown) => void) => void;
}

// Extend Window interface for Botpress
declare global {
  interface Window {
    botpress?: WindowBotPress;
  }
}

// const CLIENT_ID = "b4c43c7e-02a6-494b-97d6-db755806e449";

function getPageSection(pathname: string): string {
  const segments = pathname.replace(/^\//, "").split("/").filter(Boolean);
  if (segments.length === 0) return "home";
  const firstSegment = segments[0];
  if (firstSegment === "contact") return "contact";
  if (firstSegment === "sell") return "seller";
  if (firstSegment === "about") return "about";
  if (firstSegment === "faq" || firstSegment === "faqs") return "faq";
  if (firstSegment === "blog") return "blog";
  if (firstSegment === "privacy" || firstSegment === "terms") return "legal";
  const cityPatterns = ["bengaluru", "bangalore", "mumbai", "delhi", "hyderabad", "pune", "chennai"];
  if (cityPatterns.includes(firstSegment.toLowerCase())) {
    const secondSegment = segments[1];
    if (secondSegment === "units" || secondSegment === "unit") return "property_listing";
    if (secondSegment === "projects" || secondSegment === "project") return "project_listing";
    if (secondSegment === "search") return "search";
    return "city_browse";
  }
  if (firstSegment === "dashboard") {
    const secondSegment = segments[1];
    if (secondSegment === "buyer") return "buyer_dashboard";
    if (secondSegment === "seller") return "seller_dashboard";
    return "dashboard";
  }
  if (firstSegment === "preferences" || firstSegment === "shortlist") return "buyer_preferences";
  if (firstSegment === "compare") return "property_comparison";
  return firstSegment;
}

/**
 * Hook to control the Botpress webchat state
 * Used with the React library components
 */

export interface UseBotpressWebchatReturn extends WindowBotPress {
  isOpen: boolean;
  isInitialized: boolean;
  isSecondScriptLoaded: boolean;
  isFirstScriptInjected: boolean;
  setIsFirstScriptInjected: (loaded: boolean) => void;
  setIsSecondScriptLoaded: (loaded: boolean) => void;
  setIsOpen: (isOpen: boolean) => void;
  setIsInitialized: (isInitialized: boolean) => void;
}
import { create } from "zustand";
interface BotpressStore {
  isOpen: boolean;
  isInitialized: boolean;
  isSecondScriptLoaded: boolean;
  isFirstScriptInjected: boolean;
  setIsFirstScriptInjected: (loaded: boolean) => void;
  setIsSecondScriptLoaded: (loaded: boolean) => void;
  setIsOpen: (isOpen: boolean) => void;
  setIsInitialized: (isInitialized: boolean) => void;
}
export const useBotpressStore = create<BotpressStore>(set => ({
  isOpen: false,
  isInitialized: false,
  isSecondScriptLoaded: false,
  isFirstScriptInjected: false,
  setIsFirstScriptInjected: loaded => set({
    isFirstScriptInjected: loaded
  }),
  setIsSecondScriptLoaded: loaded => set({
    isSecondScriptLoaded: loaded
  }),
  setIsOpen: isOpen => set({
    isOpen
  }),
  setIsInitialized: isInitialized => set({
    isInitialized
  })
}));
export function useBotpressWebchat() {
  const $ = _c(16);
  const store = useBotpressStore();
  const {
    user
  } = useAuth();
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname || $[1] !== user?.emails?.[0]?.address || $[2] !== user?.id || $[3] !== user?.name || $[4] !== user?.phones?.[0]?.number || $[5] !== user?.profile?.image) {
    t0 = () => {
      const url = typeof window !== "undefined" ? window.location.href : "";
      const data = {
        name: user?.name,
        pictureUrl: user?.profile?.image,
        data: {
          name: user?.name || "",
          pictureUrl: user?.profile?.image || "",
          email: user?.emails?.[0]?.address || "",
          phone: user?.phones?.[0]?.number || "",
          id: user?.id || "",
          url,
          pathname,
          pageSection: getPageSection(pathname)
        }
      };
      return data;
    };
    $[0] = pathname;
    $[1] = user?.emails?.[0]?.address;
    $[2] = user?.id;
    $[3] = user?.name;
    $[4] = user?.phones?.[0]?.number;
    $[5] = user?.profile?.image;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  const getUserData = t0;
  let t1;
  if ($[7] !== store) {
    t1 = () => {
      if (window.botpress) {
        window.botpress.open();
        store.setIsOpen(true);
      }
    };
    $[7] = store;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const open = t1;
  let t2;
  if ($[9] !== store) {
    t2 = () => {
      if (window.botpress) {
        window.botpress.close();
        store.setIsOpen(false);
      }
    };
    $[9] = store;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const close = t2;
  const sendEvent = _temp;
  const updateUser = _temp2;
  const sendMessage = _temp3;
  const on = _temp4;
  let t3;
  if ($[11] !== close || $[12] !== getUserData || $[13] !== open || $[14] !== store) {
    t3 = {
      ...store,
      getUserData,
      open,
      close,
      sendEvent,
      sendMessage,
      updateUser,
      on
    };
    $[11] = close;
    $[12] = getUserData;
    $[13] = open;
    $[14] = store;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}
function _temp4(event_0, callback) {
  window.botpress?.on(event_0, callback);
}
function _temp3(message) {
  window.botpress?.sendMessage(message);
}
function _temp2(userData) {
  window.botpress?.updateUser(userData);
}
function _temp(event) {
  window.botpress?.sendEvent(event);
}